import React, { type LazyExoticComponent } from "react";
import { type LazySuspendedExoticComponent, SilentSuspense } from "../components/silentSuspense";

export function lazy<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T>;
export function lazy<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>, autoSuspend: true): LazySuspendedExoticComponent<T>;
export function lazy<T extends React.ComponentType<any>>(factory: () => Promise<{ default: T }>, autoSuspend: false): LazyExoticComponent<T>;
export function lazy<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  autoSuspend = false
): LazyExoticComponent<T> | LazySuspendedExoticComponent<T> {
  const lazyComponent = React.lazy(loadModule);

  if (autoSuspend) {
    return SilentSuspense(lazyComponent);
  }

  return lazyComponent;

  async function loadModule() {
    try {
      return await retry(factory);
    } catch (error) {
      if (error instanceof Error) {
        throw new DynamicImportError(error.message, error);
      } else {
        throw new DynamicImportError("Unknown error while loading dynamically imported module");
      }
    }
  }
}

export class DynamicImportError extends Error {
  public innerError?: Error;

  constructor(message: string, innerError?: Error) {
    super(message);
    this.name = "DynamicImportError";
    this.innerError = innerError;
  }

  public get [Symbol.toStringTag]() {
    return "DynamicImportError";
  }
}

async function retry<T>(fn: () => Promise<T>, attemptsLeft = 5, delay = 200): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    // No more retries left
    if (attemptsLeft <= 1) {
      throw error;
    }

    const cappedDelay = Math.min(5000, delay);
    const jitteredDelay = Math.random() * cappedDelay;

    // Delay before retrying
    await new Promise(r => setTimeout(r, jitteredDelay));

    // Retry
    return retry(fn, attemptsLeft - 1, delay * 2);
  }
}
